/**
 * Orders Selectors
 * Memoized selectors for reading order state
 * Reused by: copilot, presentation
 */

import { createSelector } from '@reduxjs/toolkit'
import type { OrderStatus } from '../domain/types'
import { selectUserRoleType, ROLE_TYPES } from '@ufc/domains/auth'

// Root state type - will be provided by app
interface RootState {
  orders: import('./orders.slice').OrdersState
  auth?: any // Depending on how auth is integrated
}

function isSelectableOnPage(status: OrderStatus): boolean {
  return status === 'pending'
}

// Base selector
const selectOrdersState = (state: RootState) => state.orders

// Items selectors
export const selectOrders = createSelector(
  selectOrdersState,
  (state) => state.items
)

export const selectOrdersCount = createSelector(
  selectOrders,
  (orders) => orders.length
)

export const selectSelectedOrder = createSelector(
  selectOrdersState,
  (state) => state.selectedOrder
)

// Filter selectors
export const selectOrdersFilter = createSelector(
  selectOrdersState,
  (state) => state.filter
)

// Pagination selector
export const selectOrdersPagination = createSelector(
  selectOrdersState,
  (state) => state.pagination
)

// Note: Filtering is now done server-side via pagination API
// This selector returns the items as-is (already filtered by the API)
export const selectFilteredOrders = createSelector(
  [selectOrders],
  (orders) => orders
)

// Status-based selectors
export const selectOrdersByStatus = (status: OrderStatus) =>
  createSelector(selectOrders, (orders) =>
    orders.filter(o => o.status === status)
  )

export const selectPendingOrders = createSelector(
  selectOrders,
  (orders) => orders.filter(o => o.status === 'pending')
)

export const selectPendingOrdersCount = createSelector(
  selectPendingOrders,
  (orders) => orders.length
)

export const selectDeliveredOrders = createSelector(
  selectOrders,
  (orders) => orders.filter(o => o.status === 'delivered')
)

// Stats selectors
export const selectOrderStats = createSelector(
  selectOrdersState,
  (state) => state.stats
)

// Loading/error selectors
export const selectOrdersLoading = createSelector(
  selectOrdersState,
  (state) => state.loading
)

export const selectOrdersError = createSelector(
  selectOrdersState,
  (state) => state.error
)

export const selectVariantFilterOptions = createSelector(
  selectOrdersState,
  (state) => state.variantFilterOptions
)

// Computed selectors
export const selectTotalOrdersValue = createSelector(
  selectOrders,
  (orders) => orders.reduce((sum, order) => sum + order.totalAmount.amount, 0)
)

export const selectOrderById = (orderId: string) =>
  createSelector(selectOrders, (orders) =>
    orders.find(o => o.id === orderId)
  )

// Recent orders (last 5)
export const selectRecentOrders = createSelector(
  selectOrders,
  (orders) => [...orders]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)
)

// Multi-select selectors for bulk actions
export const selectSelectedOrderIds = createSelector(
  selectOrdersState,
  (state) => state.selectedOrderIds
)

export const selectSelectedOrdersCount = createSelector(
  selectSelectedOrderIds,
  (ids) => ids.length
)

export const selectIsAllPagesSelected = createSelector(
  selectOrdersState,
  (state) => state.isAllPagesSelected
)

export const selectBulkDownloading = createSelector(
  selectOrdersState,
  (state) => state.bulkDownloading
)

export const selectBulkCancelling = createSelector(
  selectOrdersState,
  (state) => state.bulkCancelling
)

export const selectLastBulkCancelResult = createSelector(
  selectOrdersState,
  (state) => state.lastBulkCancelResult
)

// Check if a specific order is selected
export const selectIsOrderSelected = (orderId: string) =>
  createSelector(selectSelectedOrderIds, (ids) => ids.includes(orderId))

// Check if all orders on current page are selected
export const selectIsAllOnPageSelected = createSelector(
  [selectOrders, selectSelectedOrderIds],
  (orders, selectedIds) => {
    const selectableOrders = orders.filter((order) => isSelectableOnPage(order.status))
    if (selectableOrders.length === 0) return false
    return selectableOrders.every((order) => selectedIds.includes(order.id))
  }
)

// Check if some (but not all) orders on page are selected - for indeterminate checkbox state
export const selectIsSomeOnPageSelected = createSelector(
  [selectOrders, selectSelectedOrderIds],
  (orders, selectedIds) => {
    const selectableOrders = orders.filter((order) => isSelectableOnPage(order.status))
    if (selectableOrders.length === 0) return false
    const selectedOnPage = selectableOrders.filter((order) => selectedIds.includes(order.id))
    return selectedOnPage.length > 0 && selectedOnPage.length < selectableOrders.length
  }
)

// Can download bulk PDF (at least 1 selected, max 25)
const MAX_BULK_PDF_ORDERS = 25
export const selectCanDownloadBulkPDF = createSelector(
  selectSelectedOrdersCount,
  (count) => count > 0 && count <= MAX_BULK_PDF_ORDERS
)

// Get selected orders (the actual order objects)
export const selectSelectedOrders = createSelector(
  [selectOrders, selectSelectedOrderIds],
  (orders, selectedIds) => orders.filter(order => selectedIds.includes(order.id))
)

export const selectCanBulkCancelSelected = createSelector(
  [selectSelectedOrders, selectUserRoleType],
  (selectedOrders, roleType) =>
    roleType !== ROLE_TYPES.PACKHOUSE_MANAGER &&
    selectedOrders.length > 0 && 
    selectedOrders.every((order) => order.status === 'pending')
)

// ============================================================
// Force-status-update selectors (super_admin only)
// ============================================================

export const selectBulkForceUpdating = createSelector(
  selectOrdersState,
  (state) => state.bulkForceUpdating ?? false
)

export const selectLastBulkForceUpdateResult = createSelector(
  selectOrdersState,
  (state) => state.lastBulkForceUpdateResult ?? null
)

export const selectCanForceStatusUpdate = createSelector(
  [selectSelectedOrdersCount, selectBulkForceUpdating],
  (count, updating) => count > 0 && !updating
)

// Checks if ALL orders on the current page are selected (any status — no restriction)
export const selectIsAllOnPageSelectedAnyStatus = createSelector(
  [selectOrders, selectSelectedOrderIds],
  (orders, selectedIds) => {
    if (orders.length === 0) return false
    return orders.every((order) => selectedIds.includes(order.id))
  }
)

// Checks if SOME (but not all) orders on the current page are selected (any status)
export const selectIsSomeOnPageSelectedAnyStatus = createSelector(
  [selectOrders, selectSelectedOrderIds],
  (orders, selectedIds) => {
    if (orders.length === 0) return false
    const selectedOnPage = orders.filter((order) => selectedIds.includes(order.id))
    return selectedOnPage.length > 0 && selectedOnPage.length < orders.length
  }
)
